import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar, AlertTriangle, CheckCircle2, Clock, Shield,
  Zap, Flame, Droplets, Snowflake, Home, Bell, Plus,
  PlayCircle, RefreshCw, FileText, BarChart3, ExternalLink, Wrench
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping - includes all PPM categories
const CATEGORY_ICONS = {
  // New PPM Master List categories
  'Compliance': <Shield className="h-4 w-4 text-red-500" />,
  'Structural': <Home className="h-4 w-4 text-slate-600" />,
  'Services': <Zap className="h-4 w-4 text-blue-500" />,
  'Grounds': <Home className="h-4 w-4 text-green-600" />,
  'Seasonal': <Snowflake className="h-4 w-4 text-cyan-500" />,
  // Legacy categories
  'Electrical': <Zap className="h-4 w-4 text-yellow-500" />,
  'Gas': <Flame className="h-4 w-4 text-orange-500" />,
  'Water': <Droplets className="h-4 w-4 text-blue-500" />,
  'Drainage': <Droplets className="h-4 w-4 text-cyan-500" />,
  'HVAC': <Snowflake className="h-4 w-4 text-sky-500" />,
  'Fabric': <Home className="h-4 w-4 text-amber-600" />,
  'Fire & Safety': <Shield className="h-4 w-4 text-red-500" />,
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-600',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Critical': 'bg-red-100 text-red-700',
};

// All category tabs for the PPM Task Library
const PPM_CATEGORIES = [
  { id: 'all', label: 'All Tasks' },
  { id: 'compliance', label: 'Compliance', className: 'text-red-600' },
  { id: 'structural', label: 'Structural' },
  { id: 'services', label: 'Services' },
  { id: 'grounds', label: 'Grounds' },
  { id: 'seasonal', label: 'Seasonal', className: 'text-cyan-600' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'gas', label: 'Gas' },
  { id: 'water', label: 'Water' },
  { id: 'fabric', label: 'Fabric' },
  { id: 'fire & safety', label: 'Fire & Safety' },
];

export default function PPMDashboardPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showTriggerJobs, setShowTriggerJobs] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showAlertActions, setShowAlertActions] = useState(false);
  const [showCategoryTasks, setShowCategoryTasks] = useState(false);
  const [categoryTasksData, setCategoryTasksData] = useState({ category: '', tasks: [], overdueItems: [] });
  const [kpiFilter, setKpiFilter] = useState(null); // For KPI card filtering
  const [showKpiDialog, setShowKpiDialog] = useState(false);
  const [kpiDialogData, setKpiDialogData] = useState({ title: '', items: [], type: '' });
  const token = localStorage.getItem('token');

  const headers = { Authorization: `Bearer ${token}` };

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, tasksRes, assetsRes] = await Promise.all([
        axios.get(`${API}/api/ppm/dashboard`, { headers }),
        axios.get(`${API}/api/ppm/tasks`, { headers }),
        axios.get(`${API}/api/ppm/assets?limit=50`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setTasks(tasksRes.data.tasks || []);
      setAssets(assetsRes.data.assets || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
      toast.error('Failed to load PPM dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const triggerPPMJobs = async (options = {}) => {
    try {
      const res = await axios.post(`${API}/api/ppm/trigger-jobs`, options, { headers });
      const jobCount = res.data.total_jobs_created || res.data.jobs_created || 0;
      toast.success(`${jobCount} PPM jobs created`);
      setShowTriggerJobs(false);
      loadDashboard();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') :
                       'Failed to trigger PPM jobs';
      toast.error(errorMsg);
    }
  };

  // Handle clicking on a compliance alert
  const handleAlertClick = (alert) => {
    setSelectedAlert(alert);
    setShowAlertActions(true);
  };

  // Create a job from the compliance alert
  const createJobFromAlert = async () => {
    if (!selectedAlert) return;
    
    try {
      // Determine job type based on task
      let jobType = 'COMPLIANCE';
      if (selectedAlert.task_name?.includes('EICR') || selectedAlert.issue?.includes('EICR')) {
        jobType = 'EICR_PARK_HOME';
      } else if (selectedAlert.task_name?.includes('Gas') || selectedAlert.issue?.includes('Gas')) {
        jobType = 'LPG_GAS_CHECK';
      }
      
      // Extract site name and unit number
      const assetName = selectedAlert.asset_name || '';
      const siteName = selectedAlert.site_name || assetName.split(' - ')[1] || 'Unknown Site';
      const unitMatch = assetName.match(/Unit (\d+)/);
      const unitNumber = unitMatch ? unitMatch[1] : '';
      
      const jobData = {
        job_type: jobType,
        site_name: siteName,
        postcode: selectedAlert.postcode || 'TBC',  // Required field
        unit_number: unitNumber,
        description: `${selectedAlert.task_name || selectedAlert.issue || 'Compliance Task'} - ${selectedAlert.reason || 'Certificate missing/not found'} - ${assetName}`,
        priority: 'High',
        status: 'pending',
        source: 'ppm_compliance',
        compliance_required: true,
        plot_number: unitNumber,
      };
      
      const res = await axios.post(`${API}/api/fm/jobs`, jobData, { headers });
      toast.success(`Job created: ${res.data.job?.job_reference || 'Success'}`);
      setShowAlertActions(false);
      setSelectedAlert(null);
      
      // Navigate to the FM System page
      navigate('/fm');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') :
                       'Failed to create job';
      toast.error(errorMsg);
    }
  };

  // Mark as resolved (upload certificate)
  const markAsResolved = async () => {
    if (!selectedAlert) return;
    
    // For now, navigate to documents to upload certificate
    toast.info('Please upload the compliance certificate');
    setShowAlertActions(false);
    navigate('/documents');
  };

  // Handle category click - show tasks for that category
  const handleCategoryClick = (category, stats) => {
    // Find all overdue items for this category
    const overdueItems = dashboard?.overdue_30_days?.filter(
      item => item.category === category
    ) || [];
    
    // Find task definitions for this category
    const categoryTasks = tasks.filter(t => t.category === category);
    
    setCategoryTasksData({
      category,
      tasks: categoryTasks,
      overdueItems,
      stats,
    });
    setShowCategoryTasks(true);
  };

  // Handle KPI card clicks - show detailed list
  const handleKpiClick = async (type) => {
    setKpiFilter(type);
    
    let title = '';
    let items = [];
    
    switch(type) {
      case 'assets':
        title = 'All Assets';
        items = assets.map(a => ({
          id: a.id,
          name: a.name,
          subtitle: `${a.site_name} - ${a.postcode}`,
          status: a.compliance_status || 'Unknown',
          link: null
        }));
        break;
      case 'ppm_tasks':
        title = 'PPM Tasks Library';
        items = tasks.map(t => ({
          id: t.id,
          name: t.name,
          subtitle: `${t.category} - ${t.frequency}`,
          status: t.is_legal_requirement ? 'Legal' : 'Standard',
          link: null
        }));
        break;
      case 'overdue':
        title = 'Overdue Tasks';
        items = (dashboard?.overdue_30_days || []).map(o => ({
          id: o.asset_id,
          name: o.task_name,
          subtitle: `${o.asset_name} - ${o.days_overdue} days overdue`,
          status: 'Overdue',
          priority: o.priority,
          link: o.fm_job_id ? `/fm?job=${o.fm_job_id}` : null,
          fm_job_id: o.fm_job_id
        }));
        break;
      case 'due_soon':
        title = 'Tasks Due Soon (Next 30 Days)';
        items = (dashboard?.upcoming_tasks || []).map(u => ({
          id: u.schedule_id,
          name: u.task_name,
          subtitle: `${u.asset_name} - Due: ${new Date(u.due_date).toLocaleDateString('en-GB')}`,
          status: 'Upcoming',
          link: null
        }));
        break;
      case 'non_compliant':
        title = 'Non-Compliant Assets';
        items = (dashboard?.compliance_alerts || []).map(a => ({
          id: a.asset_id,
          name: a.asset_name,
          subtitle: a.issue || a.reason || 'Missing compliance certificate',
          status: 'Non-Compliant',
          priority: 'Critical',
          link: null
        }));
        break;
      case 'legal':
        title = 'Legal/Compliance Tasks';
        items = tasks.filter(t => t.is_legal_requirement).map(t => ({
          id: t.id,
          name: t.name,
          subtitle: `${t.category} - ${t.frequency}`,
          status: 'Legal Requirement',
          link: null
        }));
        break;
      default:
        return;
    }
    
    setKpiDialogData({ title, items, type });
    setShowKpiDialog(true);
  };

  // Navigate to FM job from KPI dialog
  const navigateToFMJob = (jobId) => {
    setShowKpiDialog(false);
    navigate(`/fm?job=${jobId}`);
  };

  // Add Asset Form
  const AddAssetForm = () => {
    const [formData, setFormData] = useState({
      site_id: '',
      site_name: '',
      unit_number: '',
      unit_type: 'Park Home',
      postcode: '',
      region: '',
      owner_name: '',
      owner_email: '',
    });
    const [selectedTasks, setSelectedTasks] = useState([]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        // Create asset
        const assetRes = await axios.post(`${API}/api/ppm/assets`, {
          ...formData,
          site_id: formData.site_id || formData.site_name.toLowerCase().replace(/\s+/g, '_'),
        }, { headers });

        // Assign selected tasks
        if (selectedTasks.length > 0) {
          await axios.post(`${API}/api/ppm/assets/${assetRes.data.asset.id}/assign-tasks`, {
            asset_id: assetRes.data.asset.id,
            task_ids: selectedTasks,
          }, { headers });
        }

        toast.success('Asset created and PPM tasks assigned');
        setShowAddAsset(false);
        loadDashboard();
      } catch (err) {
        const detail = err.response?.data?.detail;
        const errorMsg = typeof detail === 'string' ? detail : 
                         Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') :
                         'Failed to create asset';
        toast.error(errorMsg);
      }
    };

    const complianceTasks = tasks.filter(t => t.compliance);
    const nonComplianceTasks = tasks.filter(t => !t.compliance);

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Site Name *</Label>
            <Input
              value={formData.site_name}
              onChange={e => setFormData({...formData, site_name: e.target.value})}
              placeholder="e.g., Seaside Holiday Park"
              required
            />
          </div>
          <div>
            <Label>Unit Number *</Label>
            <Input
              value={formData.unit_number}
              onChange={e => setFormData({...formData, unit_number: e.target.value})}
              placeholder="e.g., 42"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Unit Type</Label>
            <Select value={formData.unit_type} onValueChange={v => setFormData({...formData, unit_type: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Park Home">Park Home</SelectItem>
                <SelectItem value="Lodge">Lodge</SelectItem>
                <SelectItem value="Static Caravan">Static Caravan</SelectItem>
                <SelectItem value="Chalet">Chalet</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Postcode *</Label>
            <Input
              value={formData.postcode}
              onChange={e => setFormData({...formData, postcode: e.target.value.toUpperCase()})}
              placeholder="e.g., FY1 2AA"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Owner Name</Label>
            <Input
              value={formData.owner_name}
              onChange={e => setFormData({...formData, owner_name: e.target.value})}
              placeholder="Contact name"
            />
          </div>
          <div>
            <Label>Owner Email</Label>
            <Input
              type="email"
              value={formData.owner_email}
              onChange={e => setFormData({...formData, owner_email: e.target.value})}
              placeholder="email@example.com"
            />
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Assign PPM Tasks</Label>
          <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
            <p className="text-xs font-semibold text-red-600 mb-2">⚠️ Compliance Tasks (LEGAL)</p>
            {complianceTasks.map(task => (
              <label key={task.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                <input
                  type="checkbox"
                  checked={selectedTasks.includes(task.id)}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedTasks([...selectedTasks, task.id]);
                    } else {
                      setSelectedTasks(selectedTasks.filter(t => t !== task.id));
                    }
                  }}
                  className="rounded"
                />
                <span className="text-red-600">{task.name}</span>
                <span className="text-xs text-slate-500">({task.frequency_months}mo)</span>
              </label>
            ))}
            <p className="text-xs font-semibold text-slate-600 mt-3 mb-2">Preventive Tasks</p>
            {nonComplianceTasks.map(task => (
              <label key={task.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                <input
                  type="checkbox"
                  checked={selectedTasks.includes(task.id)}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedTasks([...selectedTasks, task.id]);
                    } else {
                      setSelectedTasks(selectedTasks.filter(t => t !== task.id));
                    }
                  }}
                  className="rounded"
                />
                <span>{task.name}</span>
                <span className="text-xs text-slate-500">({task.frequency_months}mo)</span>
              </label>
            ))}
          </div>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => setSelectedTasks(tasks.map(t => t.id))}
            className="mt-1 p-0 h-auto"
          >
            Select All Tasks
          </Button>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => setShowAddAsset(false)}>
            Cancel
          </Button>
          <Button type="submit">Create Asset</Button>
        </div>
      </form>
    );
  };

  // Trigger Jobs Dialog
  const TriggerJobsDialog = () => {
    const [options, setOptions] = useState({
      include_overdue_only: false,
      task_ids: [],
    });

    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          This will scan all assets and create FM jobs for due/overdue PPM tasks.
        </p>

        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={options.include_overdue_only}
              onChange={e => setOptions({...options, include_overdue_only: e.target.checked})}
              className="rounded"
            />
            <span className="text-sm">Only trigger overdue tasks</span>
          </label>
        </div>

        <div className="bg-amber-50 p-3 rounded-lg text-sm">
          <p className="font-medium text-amber-800">This will create:</p>
          <ul className="list-disc list-inside text-amber-700 mt-1">
            <li>{dashboard?.summary?.total_overdue || 0} overdue task jobs</li>
            <li>{dashboard?.summary?.total_upcoming || 0} upcoming task jobs (next 30 days)</li>
          </ul>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setShowTriggerJobs(false)}>
            Cancel
          </Button>
          <Button onClick={() => triggerPPMJobs(options)} className="bg-[#1F7A78] hover:bg-[#005a5c]">
            <PlayCircle className="h-4 w-4 mr-2" />
            Trigger PPM Jobs
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#1F7A78] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading PPM System...</p>
        </div>
      </div>
    );
  }

  const complianceRate = dashboard?.summary?.compliance_rate || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F7A78] to-[#0dbdad] text-white p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">PPM Schedule System</h1>
              <p className="text-white/80 text-xs sm:text-sm">Planned Preventive Maintenance - UK Park & Leisure Portfolio</p>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <Dialog open={showTriggerJobs} onOpenChange={setShowTriggerJobs}>
                <DialogTrigger asChild>
                  <Button variant="secondary" className="flex-1 sm:flex-none text-xs sm:text-sm">
                    <PlayCircle className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Trigger PPM Jobs</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Bulk Trigger PPM Jobs</DialogTitle>
                  </DialogHeader>
                  <TriggerJobsDialog />
                </DialogContent>
              </Dialog>
              <Dialog open={showAddAsset} onOpenChange={setShowAddAsset}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-[#1F7A78] hover:bg-white/90 flex-1 sm:flex-none text-xs sm:text-sm">
                    <Plus className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Add Asset</span><span className="sm:hidden">Add</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Asset for PPM Tracking</DialogTitle>
                  </DialogHeader>
                  <AddAssetForm />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${kpiFilter === 'assets' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKpiClick('assets')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-50 rounded-lg">
                  <Home className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.total_assets || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Assets</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${kpiFilter === 'ppm_tasks' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKpiClick('ppm_tasks')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-amber-50 rounded-lg">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.total_ppm_tasks || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-all ${kpiFilter === 'overdue' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKpiClick('overdue')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-red-50 rounded-lg">
                  <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-red-600">{dashboard?.summary?.overdue || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${kpiFilter === 'due_soon' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKpiClick('due_soon')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-orange-50 rounded-lg">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.summary?.total_upcoming || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Due Soon</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm border-l-4 border-l-red-600 cursor-pointer hover:shadow-md transition-all ${kpiFilter === 'non_compliant' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKpiClick('non_compliant')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-red-50 rounded-lg">
                  <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 animate-pulse" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-red-600">{dashboard?.summary?.non_compliant_assets || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Non-Compliant</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${kpiFilter === 'legal' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKpiClick('legal')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-emerald-50 rounded-lg">
                  <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.compliance_tasks || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Legal</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Compliance Alerts */}
        {dashboard?.compliance_alerts?.length > 0 && (
          <Card className="border-0 shadow-sm border-l-4 border-l-red-600 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5 animate-pulse" />
                CRITICAL: Compliance Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboard.compliance_alerts.slice(0, 10).map((alert, idx) => (
                  <div 
                    key={idx} 
                    className="bg-red-50 p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => handleAlertClick(alert)}
                    data-testid={`compliance-alert-${idx}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-red-800">{alert.task_name || alert.issue}</p>
                      <p className="text-sm text-red-600">
                        {alert.asset_name || alert.site_name} {alert.unit_number ? `- Unit ${alert.unit_number}` : ''} 
                        {alert.days_until_due ? ` • ${Math.abs(alert.days_until_due)} days overdue` : ''}
                      </p>
                      {alert.linked_job_ref && (
                        <p className="text-xs text-emerald-600 mt-1">Job: {alert.linked_job_ref}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {alert.linked_job_id ? (
                        <Badge className="bg-emerald-600 text-white">JOB CREATED</Badge>
                      ) : (
                        <Badge className="bg-red-600 text-white">LEGAL REQUIREMENT</Badge>
                      )}
                      <ExternalLink className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                ))}
                {dashboard.compliance_alerts.length > 10 && (
                  <p className="text-sm text-red-600 text-center mt-2">
                    + {dashboard.compliance_alerts.length - 10} more alerts
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alert Actions Dialog */}
        <Dialog open={showAlertActions} onOpenChange={setShowAlertActions}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Compliance Action Required
              </DialogTitle>
            </DialogHeader>
            {selectedAlert && (
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="font-semibold text-red-800">{selectedAlert.task_name || selectedAlert.issue}</p>
                  <p className="text-sm text-red-600 mt-1">
                    {selectedAlert.asset_name || selectedAlert.site_name}
                  </p>
                  {selectedAlert.reason && (
                    <p className="text-xs text-red-500 mt-2">{selectedAlert.reason}</p>
                  )}
                  {selectedAlert.linked_job_ref && (
                    <p className="text-xs text-emerald-600 mt-2 font-medium">
                      Job Created: {selectedAlert.linked_job_ref}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Select an action:</p>
                  
                  {selectedAlert.linked_job_id ? (
                    // Job already exists - show View Job button
                    <Button 
                      onClick={() => {
                        setShowAlertActions(false);
                        navigate(`/fm?job=${selectedAlert.linked_job_id}`);
                      }}
                      className="w-full bg-[#1F7A78] hover:bg-[#16615f]"
                      data-testid="view-linked-job"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Job ({selectedAlert.linked_job_ref})
                    </Button>
                  ) : (
                    // No job yet - show Create Job button
                    <Button 
                      onClick={createJobFromAlert}
                      className="w-full bg-[#1F7A78] hover:bg-[#16615f]"
                      data-testid="create-job-from-alert"
                    >
                      <Wrench className="h-4 w-4 mr-2" />
                      Create Job for this Task
                    </Button>
                  )}
                  
                  <Button 
                    onClick={markAsResolved}
                    variant="outline"
                    className="w-full"
                    data-testid="mark-resolved"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Upload Certificate / Mark Resolved
                  </Button>
                  
                  <Button 
                    onClick={() => {
                      setShowAlertActions(false);
                      navigate('/fm');
                    }}
                    variant="ghost"
                    className="w-full"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View All Jobs
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowAlertActions(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Tasks Dialog */}
        <Dialog open={showCategoryTasks} onOpenChange={setShowCategoryTasks}>
          <DialogContent className="max-w-2xl max-h-[80vh] z-[60]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {CATEGORY_ICONS[categoryTasksData.category] || <FileText className="h-5 w-5" />}
                {categoryTasksData.category} Tasks
                {categoryTasksData.stats?.overdue > 0 && (
                  <Badge className="bg-red-100 text-red-700 ml-2">{categoryTasksData.stats.overdue} Overdue</Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4 relative z-10">
                {/* Overdue Items with Jobs */}
                {categoryTasksData.overdueItems?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-600 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Overdue Tasks ({categoryTasksData.overdueItems.length})
                    </h4>
                    <div className="space-y-2">
                      {categoryTasksData.overdueItems.map((item, idx) => (
                        <div 
                          key={idx}
                          data-testid={`category-task-${idx}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors relative z-20 ${
                            item.linked_job_id 
                              ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' 
                              : 'bg-red-50 border-red-200 hover:bg-red-100'
                          }`}
                          onClick={() => {
                            if (item.linked_job_id) {
                              setShowCategoryTasks(false);
                              navigate(`/fm?job=${item.linked_job_id}`);
                            } else {
                              setShowCategoryTasks(false);
                              handleAlertClick(item);
                            }
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{item.task_name}</p>
                              <p className="text-sm text-slate-600">{item.asset_name || item.site_name}</p>
                              {item.reason && (
                                <p className="text-xs text-slate-500 mt-1">{item.reason}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {item.linked_job_id ? (
                                <>
                                  <Badge className="bg-emerald-600 text-white">JOB CREATED</Badge>
                                  <span className="text-xs text-emerald-700">{item.linked_job_ref}</span>
                                </>
                              ) : (
                                <Badge className="bg-red-600 text-white">NEEDS ACTION</Badge>
                              )}
                              <ExternalLink className="h-4 w-4 text-slate-400" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Task Definitions */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Task Types in this Category</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {categoryTasksData.tasks?.map((task, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${task.compliance ? 'border-red-200 bg-red-50/30' : 'bg-slate-50'}`}
                      >
                        <p className="font-medium text-sm">{task.name}</p>
                        <p className="text-xs text-slate-500 mt-1">{task.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-slate-400">Every {task.frequency_months} months</span>
                          {task.cost_min && task.cost_max && (
                            <span className="text-xs text-slate-400">£{task.cost_min}-£{task.cost_max}</span>
                          )}
                          {task.compliance && (
                            <Badge className="text-xs bg-red-100 text-red-700">LEGAL</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCategoryTasks(false)}>
                Close
              </Button>
              {categoryTasksData.overdueItems?.length > 0 ? (
                <Button 
                  onClick={() => {
                    setShowCategoryTasks(false);
                    // Navigate to FM with category filter
                    navigate(`/fm?category=${encodeURIComponent(categoryTasksData.category)}`);
                  }}
                  className="bg-[#1F7A78] hover:bg-[#16615f]"
                >
                  View {categoryTasksData.category} Jobs ({categoryTasksData.overdueItems.length})
                </Button>
              ) : (
                <Button 
                  onClick={() => setShowCategoryTasks(false)}
                  variant="outline"
                >
                  No Jobs in this Category
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Task Lists */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Overdue Tasks */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2 px-3 sm:px-6">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
                  Overdue Tasks
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <ScrollArea className="h-[200px] sm:h-[250px]">
                  {dashboard?.overdue_30_days?.length > 0 ? (
                    <div className="space-y-2">
                      {dashboard.overdue_30_days.map((item, idx) => (
                        <div 
                          key={idx} 
                          className="p-3 border rounded-lg bg-red-50/50 border-red-200 cursor-pointer hover:bg-red-100/50 transition-colors"
                          onClick={() => handleAlertClick(item)}
                          data-testid={`overdue-task-${idx}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {CATEGORY_ICONS[item.category] || <FileText className="h-4 w-4 text-slate-500" />}
                              <span className="font-medium">{item.task_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {item.linked_job_id ? (
                                <Badge className="bg-emerald-600 text-white">JOB CREATED</Badge>
                              ) : (
                                <Badge className={item.compliance ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'}>
                                  {item.days_until_due ? `${Math.abs(item.days_until_due)}d overdue` : 'OVERDUE'}
                                </Badge>
                              )}
                              <ExternalLink className="h-4 w-4 text-slate-400" />
                            </div>
                          </div>
                          <p className="text-sm text-slate-600">
                            {item.asset_name || item.site_name} {item.unit_number ? `- Unit ${item.unit_number}` : ''} 
                            {item.postcode ? ` • ${item.postcode}` : ''}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {item.linked_job_ref && (
                              <span className="text-xs text-emerald-600 font-medium">Job: {item.linked_job_ref}</span>
                            )}
                            {item.cost_min && item.cost_max && (
                              <span className="text-xs text-slate-500">£{item.cost_min}-£{item.cost_max}</span>
                            )}
                            {item.reason && !item.linked_job_ref && (
                              <span className="text-xs text-red-600">{item.reason}</span>
                            )}
                            {item.compliance && !item.linked_job_id && (
                              <Badge className="text-xs bg-red-100 text-red-700">COMPLIANCE</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                      <p>No overdue tasks!</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Upcoming Tasks */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2 px-3 sm:px-6">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500" />
                  Upcoming (Next 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <ScrollArea className="h-[200px] sm:h-[250px]">
                  {dashboard?.upcoming_30_days?.length > 0 ? (
                    <div className="space-y-2">
                      {dashboard.upcoming_30_days.map((item, idx) => (
                        <div key={idx} className="p-3 border rounded-lg hover:bg-slate-50">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              {CATEGORY_ICONS[item.category]}
                              <span className="font-medium">{item.task_name}</span>
                            </div>
                            <Badge className="bg-orange-100 text-orange-700">
                              {item.days_until_due}d
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">
                            {item.site_name} - Unit {item.unit_number}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-500">Due: {new Date(item.next_due).toLocaleDateString()}</span>
                            <Badge className={`text-xs ${PRIORITY_COLORS[item.priority]}`}>{item.priority}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-slate-500">No upcoming tasks in next 30 days</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Stats & Categories */}
          <div className="space-y-6">
            {/* Category Breakdown */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.category_stats && Object.entries(dashboard.category_stats).map(([cat, stats]) => (
                    <div 
                      key={cat} 
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => handleCategoryClick(cat, stats)}
                      data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center gap-2">
                        {CATEGORY_ICONS[cat] || <FileText className="h-4 w-4 text-slate-500" />}
                        <span className="text-sm font-medium">{cat}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{stats.tasks} tasks</span>
                        {stats.overdue > 0 && (
                          <Badge className="bg-red-100 text-red-700 text-xs">{stats.overdue}</Badge>
                        )}
                        {stats.compliance > 0 && (
                          <Shield className="h-3 w-3 text-red-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Completions */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2 px-3 sm:px-6">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
                  Recent Completions
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                <ScrollArea className="h-[150px] sm:h-[200px]">
                  {dashboard?.recent_completions?.length > 0 ? (
                    <div className="space-y-2">
                      {dashboard.recent_completions.map((comp, idx) => (
                        <div key={idx} className="p-2 border rounded-lg text-sm">
                          <p className="font-medium">{comp.task_name}</p>
                          <p className="text-slate-500">{comp.site_name} - Unit {comp.unit_number}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-slate-400">
                              {comp.completed_date ? new Date(comp.completed_date).toLocaleDateString() : '-'}
                            </span>
                            {comp.cost && (
                              <span className="text-xs font-medium text-emerald-600">£{comp.cost}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-slate-500">No recent completions</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-[#1F7A78] to-[#0dbdad] text-white">
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <Button variant="secondary" className="w-full justify-start" onClick={() => setShowTriggerJobs(true)}>
                    <PlayCircle className="h-4 w-4 mr-2" /> Trigger All Due Jobs
                  </Button>
                  <Button variant="secondary" className="w-full justify-start" onClick={() => setShowAddAsset(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Add New Asset
                  </Button>
                  <Button variant="secondary" className="w-full justify-start" onClick={loadDashboard}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Refresh Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* PPM Task Library */}
        <Card className="border-0 shadow-sm mt-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">PPM Task Library</CardTitle>
              <Badge className="bg-[#1F7A78]">{tasks.length} Tasks</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList className="mb-4 flex-wrap h-auto gap-1">
                {PPM_CATEGORIES.map(cat => (
                  <TabsTrigger 
                    key={cat.id} 
                    value={cat.id}
                    className={cat.className || ''}
                  >
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* All Tasks Tab */}
              <TabsContent value="all">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {tasks.map(task => (
                    <div key={task.id} className={`p-3 border rounded-lg ${task.compliance ? 'border-red-200 bg-red-50/30' : ''}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {CATEGORY_ICONS[task.category] || <FileText className="h-4 w-4 text-slate-500" />}
                        <span className="font-medium text-sm">{task.name}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-2 line-clamp-2">{task.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs">Every {task.frequency_months} months</span>
                        <span className="text-xs font-medium">£{task.cost_min}-£{task.cost_max}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        <Badge className={`text-xs ${PRIORITY_COLORS[task.priority] || 'bg-slate-100 text-slate-600'}`}>
                          {task.priority}
                        </Badge>
                        {task.compliance && (
                          <Badge className="bg-red-100 text-red-700 text-xs">LEGAL</Badge>
                        )}
                        {task.seasonal_flag && (
                          <Badge className="bg-cyan-100 text-cyan-700 text-xs">SEASONAL</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Compliance Tab - Special styling */}
              <TabsContent value="compliance">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {tasks.filter(t => t.compliance || t.category === 'Compliance').map(task => (
                    <div key={task.id} className="p-3 border rounded-lg border-red-200 bg-red-50/30">
                      <div className="flex items-center gap-2 mb-1">
                        {CATEGORY_ICONS[task.category] || <Shield className="h-4 w-4 text-red-500" />}
                        <span className="font-medium text-sm">{task.name}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-2 line-clamp-2">{task.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs">Every {task.frequency_months} months</span>
                        <span className="text-xs font-medium">£{task.cost_min}-£{task.cost_max}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        <Badge className="bg-red-100 text-red-700 text-xs">LEGAL REQUIREMENT</Badge>
                        {task.requires_certificate && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">CERT REQ</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Dynamic tabs for each category */}
              {PPM_CATEGORIES.filter(c => c.id !== 'all' && c.id !== 'compliance').map(cat => (
                <TabsContent key={cat.id} value={cat.id}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {tasks.filter(t => t.category.toLowerCase() === cat.id).map(task => (
                      <div key={task.id} className={`p-3 border rounded-lg ${task.compliance ? 'border-red-200 bg-red-50/30' : ''}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {CATEGORY_ICONS[task.category] || <FileText className="h-4 w-4 text-slate-500" />}
                          <span className="font-medium text-sm">{task.name}</span>
                        </div>
                        <p className="text-xs text-slate-500 mb-2 line-clamp-2">{task.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs">Every {task.frequency_months} months</span>
                          <span className="text-xs font-medium">£{task.cost_min}-£{task.cost_max}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-2">
                          <Badge className={`text-xs ${PRIORITY_COLORS[task.priority] || 'bg-slate-100 text-slate-600'}`}>
                            {task.priority}
                          </Badge>
                          {task.compliance && (
                            <Badge className="bg-red-100 text-red-700 text-xs">LEGAL</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {tasks.filter(t => t.category.toLowerCase() === cat.id).length === 0 && (
                      <div className="col-span-full text-center py-8 text-slate-500">
                        No tasks in this category
                      </div>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* KPI Details Dialog */}
      <Dialog open={showKpiDialog} onOpenChange={setShowKpiDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {kpiDialogData.type === 'overdue' && <AlertTriangle className="h-5 w-5 text-red-600" />}
              {kpiDialogData.type === 'due_soon' && <Clock className="h-5 w-5 text-orange-600" />}
              {kpiDialogData.type === 'non_compliant' && <Bell className="h-5 w-5 text-red-600" />}
              {kpiDialogData.type === 'assets' && <Home className="h-5 w-5 text-blue-600" />}
              {kpiDialogData.type === 'ppm_tasks' && <FileText className="h-5 w-5 text-amber-600" />}
              {kpiDialogData.type === 'legal' && <Shield className="h-5 w-5 text-emerald-600" />}
              {kpiDialogData.title}
              <Badge className="ml-2">{kpiDialogData.items.length} items</Badge>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[500px] mt-4">
            <div className="space-y-2">
              {kpiDialogData.items.map((item, index) => (
                <div 
                  key={item.id || index} 
                  className={`p-3 border rounded-lg hover:bg-slate-50 ${item.link ? 'cursor-pointer' : ''} ${
                    item.status === 'Overdue' || item.status === 'Non-Compliant' ? 'border-red-200 bg-red-50/50' : ''
                  }`}
                  onClick={() => item.fm_job_id ? navigateToFMJob(item.fm_job_id) : null}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.name}</span>
                        {item.fm_job_id && (
                          <Badge className="bg-teal-100 text-teal-700 text-xs">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View FM Job
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{item.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.priority && (
                        <Badge className={`text-xs ${PRIORITY_COLORS[item.priority] || 'bg-slate-100'}`}>
                          {item.priority}
                        </Badge>
                      )}
                      <Badge className={`text-xs ${
                        item.status === 'Overdue' || item.status === 'Non-Compliant' ? 'bg-red-100 text-red-700' :
                        item.status === 'Legal' || item.status === 'Legal Requirement' ? 'bg-emerald-100 text-emerald-700' :
                        item.status === 'Upcoming' ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {item.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
              {kpiDialogData.items.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                  <p className="font-medium">No items found</p>
                  <p className="text-sm">Everything is up to date</p>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKpiDialog(false)}>Close</Button>
            {kpiDialogData.type === 'overdue' && kpiDialogData.items.length > 0 && (
              <Button className="bg-[#1F7A78]" onClick={() => { setShowKpiDialog(false); navigate('/fm'); }}>
                View All in FM System
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
